

import React from 'react'
import { useNavigate } from 'react-router-dom'
import { getOngoingBooking } from '../../../api/user/hotels'
import { useAuth } from '../../../context/AuthContext'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from "../../../hooks/useTranslation";

function HotelBooking() {
  const { token } = useAuth()
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data } = useQuery({
    queryKey: ["ongoing-booking"],
    queryFn: getOngoingBooking,
    keepPreviousData: true,
    enabled: !!token,
  });

  const booking = data?.data?.booking;

  const formatDate = (val) => {
    const date = new Date(val);
    return `${date.getDate()} ${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`;
  }

  const roomNumbers = booking?.roomIds?.map(room => room.roomNumber).join(", ");

  return (
    <div>
      {booking && (
        <div className="max-w-6xl mx-auto p-6 mt-8 mb-8">
          <div className="grid md:grid-cols-2 grid-cols-1 gap-6 bg-white rounded-lg shadow-md overflow-hidden">
            
            {/* Image */}
            <div className="flex items-center justify-center p-6 bg-gray-50 md:bg-white">
              <div className="relative w-full max-w-[300px] aspect-square mx-auto">
                {booking?.userId?.profilePicture ? (
                  <img
                    src={booking.userId.profilePicture}
                    alt={booking?.userId?.name || "Guest"}
                    className="w-full h-full object-cover rounded-lg shadow-md"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const fallback = e.currentTarget.parentElement.querySelector('.profile-fallback');
                      if (fallback) fallback.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  className="profile-fallback w-full h-full bg-gradient-to-br from-[#4CAF50] to-[#209b25] rounded-lg shadow-md items-center justify-center text-white text-4xl font-bold"
                  style={{ display: booking?.userId?.profilePicture ? 'none' : 'flex' }}
                >
                  {booking?.userId?.name?.charAt(0)?.toUpperCase() || 'G'}
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 flex flex-col justify-center">
              <h2 className="text-2xl font-semibold mb-4">
                {t("home").hotelBooking.title}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="font-medium text-gray-600">{t("home").hotelBooking.guestName}</p>
                  <p className="font-medium">{booking?.userId?.name}</p>
                </div>
                <div>
                  <p className="font-medium text-gray-600">{t("home").hotelBooking.bookingId}</p>
                  <p className="font-medium">{booking?.bookingId}</p>
                </div>
                <div>
                  <p className="font-medium text-gray-600">{t("home").hotelBooking.guestNumber}</p>
                  <p className="font-medium">{booking?.guests} {t("home").hotelBooking.guests || "Guests"}</p>
                </div>
                <div>
                  <p className="font-medium text-gray-600">{t("home").hotelBooking.hotelName}</p>
                  <p className="font-medium">{booking?.hotelId?.name}</p>
                </div>
                <div>
                  <p className="font-medium text-gray-600">{t("home").hotelBooking.checkInDate}</p>
                  <p className="font-medium">{formatDate(booking?.checkInDate)}</p>
                </div>
                <div>
                  <p className="font-medium text-gray-600">{t("home").hotelBooking.checkOutDate}</p>
                  <p className="font-medium">{formatDate(booking?.checkOutDate)}</p>
                </div>
                <div>
                  <p className="font-medium text-gray-600">{t("home").hotelBooking.roomNumber}</p>
                  <p className="font-medium">{roomNumbers}</p>
                </div>
                <div>
                  <p className="font-medium text-gray-600">{t("home").hotelBooking.stayStatus}</p>
                  <p className="text-green-600 font-medium">{t("home").hotelBooking.currentlyStaying}</p>
                </div>
              </div>

              {booking?.hasRestaurant && (
                <div className="mt-8">
                  <p className="mb-4 text-lg">{t("home").hotelBooking.orderPrompt}</p>
                  <div className="flex gap-4 flex-wrap">
                    <button
                      onClick={() =>
                        navigate("/hotel-menu", {
                          state: { hotelId: booking?.hotelId?._id, bookingId: booking?._id },
                        })
                      }
                      className="bg-[#4CAF50] hover:bg-[#209b25] text-white font-bold py-3 px-6 rounded transition duration-200 text-lg"
                    >
                         {t("home").hotelBooking.viewMenu}
                     
                    </button>

                    <button
                      onClick={() =>
                        navigate("/user-orders", { state: { bookingId: booking?._id } })
                      }
                      className="bg-[#FF9800] hover:bg-[#e68900] text-white font-bold py-3 px-6 rounded transition duration-200 text-lg"
                    >
                      {t("home").hotelBooking.viewOrders}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {!booking && (
        <div className="max-w-6xl mx-auto p-6 mt-8 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl font-semibold mb-2">{t("home").hotelBooking.noActiveBookingTitle}</h3>
              <p className="text-gray-600 mb-4">{t("home").hotelBooking.noActiveBookingDesc}</p>
              <div className="flex gap-3 flex-wrap">
                {/* <button
                  onClick={() => navigate("/nearby-hotels")}
                  className="bg-[#4CAF50] hover:bg-[#209b25] text-white font-bold py-3 px-6 rounded transition duration-200 text-lg"
                >
                  {t("home").hotelBooking.visitRoomsCta}
                </button> */}
                <button
                  onClick={() => navigate("/nearby-hotels")}
                  className="bg-[#FF9800] hover:bg-[#e68900] text-white font-bold py-3 px-6 rounded transition duration-200 text-lg"
                >
                  {t("home").hotelBooking.scanQrCta}
                </button>
              </div>
            </div>
            <div className="flex-shrink-0">
              <img
                src="/static/media/qr-placeholder.svg"
                alt={t("home").hotelBooking.scanQrCta || "Scan QR"}
                className="w-[200px] h-[200px] object-contain opacity-80"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default HotelBooking
